import { ThemeProvider } from '@emotion/react'
import { createTheme } from '@mui/material'
import Loader from 'components/Loader/Index'
import PageSkeleton from 'components/PageSkeleton/Index'
import { setMenus, menuList } from 'features/menu/menu'
import { setMenuInProgress } from 'features/menuProgressState/menuProgressState'
import {
  setIsPaymentEnable,
  setPaymentStatusLoading,
  setRestaurant,
  setFaqUrl,
} from 'features/restaurant/restaurant'
import {
  setTab,
  setTabDetail,
  setTabItems,
  setTabSeats,
  setTabLoading,
} from 'features/tab/tab'
import useSignalR from 'hooks/useSignalR'
import Home from 'pages/Home/Index'
// import Landing from 'pages/Landing/Index'
import ScrollToTop from 'pages/ScrollToTop/Index'
import YourTabWrapper from 'pages/YourTab/YourTabWrapper'
import { useCallback, useEffect, useState, Suspense } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import ImprovedErrorBoundary from 'components/ImprovedErrorBoundary/Index'

import { useNetworkStatus } from 'hooks/useNetworkStatus'
import menuService from 'services/menu.service'
import restaurantService from 'services/restaurant.service'
import tabService from 'services/tab.service'
import guestService from 'services/guest.service'
import Landing from 'pages/Landing/Index'
import { ITabItem } from 'interfaces/tab'
import { itemsToSeat, isOrderAvailableFromTabData } from 'utility/helper'

import { lazyWithRetry } from 'utility/lazyWithRetry'
import { resolvePrintedPosTableId } from 'utility/printedTableAlias'

// Lazy load components with retry logic for critical components
const EmailReceiptSent = lazyWithRetry(
  () => import('pages/EmailReceiptSent/Index')
)
const Error = lazyWithRetry(() => import('pages/Error/Index'))
const FormRedeem = lazyWithRetry(
  () => import('pages/GuestMarketing/FormRedeem')
)
const Redeem = lazyWithRetry(() => import('pages/GuestMarketing/Redeem'))

const ContactForm = lazyWithRetry(() => import('pages/GuestMarketing/Form'))

const GuestUnsubscribe = lazyWithRetry(
  () => import('pages/GuestUnsubscribe/Index')
)

const MainMenu = lazyWithRetry(() => import('pages/MainMenu/Index'), {
  retries: 5,
  delay: 500,
})
const PaymentContainer = lazyWithRetry(
  () => import('pages/Payment/PaymentContainer'),
  { retries: 5, delay: 500 }
)
const PaymentSuccessful = lazyWithRetry(
  () => import('pages/PaymentSuccessful/Index'),
  { retries: 5, delay: 500 }
)
const SubscriptionContainer = lazyWithRetry(
  () => import('pages/Subscription/SubscriptionContainer')
)
const ThankYou = lazyWithRetry(() => import('pages/Subscription/ThankYou'))
const ThanksMessage = lazyWithRetry(() => import('pages/ThanksMessage/Index'))
const ViewMenu = lazyWithRetry(() => import('pages/ViewMenu/Index'), {
  retries: 5,
  delay: 500,
})
const OrderMenu = lazyWithRetry(() => import('pages/OrderMenu/Index'), {
  retries: 5,
  delay: 500,
})
const MyCart = lazyWithRetry(() => import('pages/MyCart/Index'), {
  retries: 5,
  delay: 500,
})
const ItemCustomization = lazyWithRetry(
  () => import('pages/ItemCustomization/Index'),
  {
    retries: 5,
    delay: 500,
  }
)
// const CheckoutContainer = lazyWithRetry(
//   () => import('pages/Checkout/CheckoutContainer'),
//   {
//     retries: 5,
//     delay: 500,
//   }
// )
const SimpleCheckoutContainer = lazyWithRetry(
  () => import('pages/Checkout/SimpleCheckoutContainer'),
  {
    retries: 5,
    delay: 500,
  }
)
const Demo = lazyWithRetry(() => import('pages/Demo/Index'))

function App() {
  const theme = createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 576,
        md: 768,
        lg: 992,
        xl: 1200,
      },
    },
    typography: {
      fontFamily: ['Poppins', 'sans-serif'].join(','),
    },
    // Use numeric spacing so any integer (e.g., 0..n) is supported across pages
    spacing: 8,
  })
  useSignalR()
  useNetworkStatus() // Initialize network monitoring
  let paymentEnableData

  if (localStorage.getItem('faqUrl')) {
    localStorage.removeItem('faqUrl')
  }
  const dispatch = useDispatch()
  const menus = useSelector(menuList)
  const searchParameters = new URLSearchParams(
    window.location.search.split('?')[1]
  )
  const establishmentId = searchParameters.get('eId')
    ? searchParameters.get('eId')
    : sessionStorage.getItem('establishmentId')
  // The id off the sticker goes through the printed-sticker corrections; the one kept in
  // session storage is already corrected and must not go through them twice.
  const tableId = searchParameters.get('pId')
    ? resolvePrintedPosTableId(establishmentId, searchParameters.get('pId'))
    : sessionStorage.getItem('posTableId')
  const tid = searchParameters.get('tid')
    ? searchParameters.get('tid')
    : sessionStorage.getItem('tid')
  const tableName = searchParameters.get('tn')
    ? searchParameters.get('tn').replace('%', '')
    : sessionStorage.getItem('tn')

  // Check if menus are available
  const hasMenus = menus && menus.length > 0

  const getRestaurantDetail = useCallback(async (id: number) => {
    if (!id) {
      return
    }
    const data = await restaurantService.getResturantById({
      establishmentId: id,
    })
    const restaurantDetailData = data.data.data
    dispatch(setRestaurant(restaurantDetailData))
    if (!restaurantDetailData.restaurantId) {
      dispatch(
        setIsPaymentEnable({
          status: paymentEnableData ? paymentEnableData.status : true,
          isTableDisable: true,
        })
      )
    }

    // storing the restaurant id in local storage to use it later in tracking event
    sessionStorage.setItem(
      'restaurantId',
      restaurantDetailData.restaurantId?.toString()
    )

    // calling tracking API for Scan Complete event
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [isOrderAvailable, setOrderAvailable] = useState(false)
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false)

  const tabDetailData = useSelector((state: any) => state.tab.tabDetailData)

  const getStripeAPIKey = () => {
    return process.env.REACT_APP_STRIPE_PUBLISHABLEKEY
  }

  // Centralized API call function to prevent duplicates
  const initializeAppData = useCallback(async () => {
    if (!establishmentId || !tid || isInitialDataLoaded) return

    try {
      // Initializing app data

      // Set payment status loading to true before making API calls
      dispatch(setPaymentStatusLoading(true))

      // Make initial API calls in parallel (removed CheckAnyOrderAvailable)
      const [paymentStatusRes, faqUrlRes] = await Promise.all([
        tabService.getIsPaymentEnable({ tableId: Number(tid) }),
        guestService.getFaqUrl(),
      ])

      // Update Redux state with all responses
      dispatch(setIsPaymentEnable(paymentStatusRes.data.data)) // This will set loading to false
      dispatch(setFaqUrl(faqUrlRes.data.data))

      // Store FAQ URL in localStorage as backup
      localStorage.setItem('faqUrl', JSON.stringify(faqUrlRes.data.data))

      // If payment is enabled, fetch tab details and determine order availability from the data
      if (paymentStatusRes.data.data.status) {
        // Set loading state before making API call
        dispatch(setTabLoading(true))

        const tabRes = await tabService.getTabs({
          posTableId: Number(tableId),
          locationId: Number(establishmentId),
        })

        const response = tabRes.data.data
        response.orders.forEach((ele) => {
          ele.orderDate = null
          ele.isSelected = true
        })

        // Determine order availability from tab data instead of separate API call
        const orderAvailable = isOrderAvailableFromTabData(response)
        setOrderAvailable(orderAvailable)

        dispatch(setTabDetail(response)) // This will automatically set loading to false
        sessionStorage.setItem('tabId', response?.tabId.toString())

        // Process tab items and seats
        if (response.orders && response.orders.length > 0) {
          let allItems: ITabItem[] = []
          response.orders.forEach((element) => {
            allItems = [...allItems, ...element.items]
          })
          dispatch(setTabSeats(itemsToSeat(allItems)))
          dispatch(setTabItems(allItems))
        } else {
          dispatch(setTabItems([]))
        }
      } else setOrderAvailable(false)

      setIsInitialDataLoaded(true)
      // App data initialized successfully
    } catch (error) {
      // Make sure to set loading to false even on error
      dispatch(setPaymentStatusLoading(false))
      setIsInitialDataLoaded(true) // Set to true even on error to prevent infinite retries
    }
  }, [establishmentId, tableId, tid, isInitialDataLoaded, dispatch])

  // Reset isOrderAvailable when table is closed
  useEffect(() => {
    if (!tabDetailData && isOrderAvailable) {
      setOrderAvailable(false)
    }
  }, [tabDetailData, isOrderAvailable])

  // Watch sessionStorage for immediate table closed detection
  useEffect(() => {
    const interval = setInterval(() => {
      const tabId = sessionStorage.getItem('tabId')
      if (tabId === 'closed' && isOrderAvailable) {
        setOrderAvailable(false)
      }
    }, 500)

    return () => clearInterval(interval)
  }, [isOrderAvailable])

  useEffect(() => {
    // Check for QR scan tracking BEFORE setting sessionStorage
    const searchParams = new URLSearchParams(window.location.search)
    const hasUrlParams =
      searchParams.get('pId') &&
      searchParams.get('eId') &&
      searchParams.get('tid')
    const isFirstTime =
      !sessionStorage.getItem('posTableId') ||
      !sessionStorage.getItem('establishmentId') ||
      !sessionStorage.getItem('tid')

    // Track QR scan only if user came via URL params and it's their first time
    if (hasUrlParams && isFirstTime && tid) {
      tabService
        .trackQRScan({ tableId: Number(tid) })
        .then(() => undefined)
        .catch(() => {
          // Fail silently - QR scan tracking is not critical to app functionality
        })
    }

    dispatch(
      setTab({
        establishmentId: Number(establishmentId),
        tableId: Number(tableId),
        tableName,
      })
    )
    if (establishmentId) {
      sessionStorage.setItem('establishmentId', establishmentId?.toString())
    }
    if (tableId) {
      sessionStorage.setItem('posTableId', tableId?.toString())
    }

    if (tid && tableName) {
      sessionStorage.setItem('tid', tid)
      sessionStorage.setItem('tn', tableName)
    }

    // Call centralized initialization instead of individual API calls
    initializeAppData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [establishmentId, tableId, tableName, tid])

  const getMenuLists = useCallback(
    async (id) => {
      try {
        dispatch(setMenuInProgress(true))
        const data = await menuService.getAllMenu({
          establishmentId: id,
          isGuestView: true,
        })
        dispatch(
          setMenus({
            menuList: data.data.data.sort((a, b) => {
              return a.position - b.position
            }),
          })
        )
        dispatch(setMenuInProgress(false))
      } catch (e) {
        dispatch(setMenuInProgress(false))
        // swallow menu load error
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(() => {
    if (establishmentId != null) {
      getRestaurantDetail(+establishmentId)
      getMenuLists(establishmentId)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [establishmentId])

  // Preload critical components to prevent dynamic import failures
  useEffect(() => {
    // Preload critical components in the background
    const preloadCriticalComponents = () => {
      // Only preload if we have menus or are likely to need these components
      if (hasMenus || isOrderAvailable) {
        MainMenu.preload().catch(() => {
          return undefined
        })
        ViewMenu.preload().catch(() => undefined)
        PaymentContainer.preload().catch(() => undefined)
        // Preload PaymentSuccessful since it's critical after payments
        PaymentSuccessful.preload().catch(() => undefined)
      }
    }

    // Delay preloading to not interfere with initial load
    const timeoutId = setTimeout(preloadCriticalComponents, 2000)
    return () => clearTimeout(timeoutId)
  }, [hasMenus, isOrderAvailable])

  const GetLandingOrViewPage = () => {
    // Check if table is closed
    const tabId = sessionStorage.getItem('tabId')
    if (tabId === 'closed') {
      return <Landing />
    }

    // Use YourTabWrapper for proper FasTab order detection
    return isOrderAvailable ? <YourTabWrapper /> : <Landing />
  }
  return (
    <ThemeProvider theme={theme}>
      {/* basename="/design/fastab" */}
      <BrowserRouter>
        <ScrollToTop />
        {/* Loader */}
        <Loader />
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
        <ImprovedErrorBoundary>
          <Suspense fallback={<PageSkeleton withFooter />}>
            <Routes>
              <Route
                path="/"
                element={tableName ? GetLandingOrViewPage() : <Home />}
              />
              {/* Only render ViewMenu route if menus are available */}
              {hasMenus && <Route path="/view-menu" element={<ViewMenu />} />}
              {hasMenus && <Route path="/order-menu" element={<OrderMenu />} />}
              {hasMenus && (
                <Route
                  path="/item-customization"
                  element={<ItemCustomization />}
                />
              )}
              {hasMenus && <Route path="/my-cart" element={<MyCart />} />}
              <Route
                path="/checkout"
                element={
                  <SimpleCheckoutContainer
                    callBackGetAPIKey={getStripeAPIKey}
                  />
                }
              />
              <Route path="/your-tab" element={<YourTabWrapper />} />
              <Route
                path="/payment"
                element={
                  <PaymentContainer callBackGetAPIKey={getStripeAPIKey} />
                }
              />
              <Route
                path="/payment-successful"
                element={<PaymentSuccessful />}
              />
              <Route path="/email-receipt" element={<EmailReceiptSent />} />
              <Route path="/thanks-message" element={<ThanksMessage />} />
              <Route path="/main-menu" element={<MainMenu />} />
              <Route path="/home" element={<Home />} />
              <Route path="/demo" element={<Demo />} />
              <Route path="*" element={<Error />} />
              <Route
                path="/unsubscribe/:campaignId/:restaurantId/:email"
                element={<GuestUnsubscribe />}
              />
              <Route path="/contact-form/:formId" element={<ContactForm />} />
              <Route
                path="/subscription"
                element={
                  <SubscriptionContainer callBackGetAPIKey={getStripeAPIKey} />
                }
              />
              <Route path="/subscription-thank-you" element={<ThankYou />} />
              <Route path="/redeem" element={<Redeem />} />
              <Route path="/form-redeem" element={<FormRedeem />} />
            </Routes>
          </Suspense>
        </ImprovedErrorBoundary>
        {/* Network status indicator */}
      </BrowserRouter>
    </ThemeProvider>
  )
}

export default App
